import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CurrencyInput } from '@/components/ui/currency-input';
import { Briefcase, ArrowLeft } from 'lucide-react';
import { API, useAuth } from '@/App';
import { getCurrencyForCountry } from '@/utils/currencyMapping';

const CreateJob = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const isEditMode = !!jobId;
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([
    'Technology', 'Finance', 'Healthcare', 'Education', 'Marketing', 'Sales',
    'Engineering', 'Design', 'Legal', 'Hospitality', 'Construction', 'Other', 'Administration'
  ]);
  const [jobTypes, setJobTypes] = useState([
    'full-time', 'part-time', 'contract', 'freelance', 'internship'
  ]);
  const [workArrangements, setWorkArrangements] = useState([
    'on-site', 'remote', 'hybrid'
  ]);

  const [formData, setFormData] = useState({
    title: '',
    company_name: '',
    company_logo: '',
    description: '',
    requirements: '',
    category: '',
    job_type: 'full-time',
    work_arrangement: 'on-site',
    location: '',
    city: '',
    country: '',
    salary_min: '',
    salary_max: '',
    salary_currency: 'USD',
    salary_period: 'year',
    visa_sponsorship: false,
    application_method: 'email',
    application_url: '',
    application_email: ''
  });

  useEffect(() => {
    if (!user) {
      toast.error('Please sign in to post jobs');
      navigate('/');
      return;
    }
    fetchCategories();
    if (isEditMode) {
      fetchJobData();
    }
  }, [user]);

  const fetchJobData = async () => {
    try {
      const response = await axios.get(`${API}/jobs/${jobId}`);
      const job = response.data;
      setFormData({
        title: job.title || '',
        company_name: job.company_name || '',
        company_logo: job.company_logo || '',
        description: job.description || '',
        requirements: job.requirements || '',
        category: job.category || '',
        job_type: job.job_type || 'full-time',
        work_arrangement: job.work_arrangement || 'on-site',
        location: job.location || '',
        city: job.city || '',
        country: job.country || '',
        salary_min: job.salary_min || '',
        salary_max: job.salary_max || '',
        salary_currency: job.salary_currency || 'USD',
        salary_period: job.salary_period || 'year',
        visa_sponsorship: job.visa_sponsorship || false,
        application_method: job.application_method || 'email',
        application_url: job.application_url || '',
        application_email: job.application_email || ''
      });
    } catch (error) {
      toast.error('Failed to load job data');
      navigate('/jobs');
    }
  };

  // Auto-select currency based on country
  useEffect(() => {
    if (formData.country) {
      const currency = getCurrencyForCountry(formData.country);
      if (currency) {
        setFormData(prev => ({ ...prev, salary_currency: currency }));
      }
    }
  }, [formData.country]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/jobs/categories`);
      setCategories(response.data.categories);
      setJobTypes(response.data.job_types);
      setWorkArrangements(response.data.work_arrangements);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.company_name || !formData.description || !formData.category || !formData.city || !formData.country) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.application_method === 'email' && !formData.application_email) {
      toast.error('Please provide an application email');
      return;
    }

    if (formData.application_method === 'external_link' && !formData.application_url) {
      toast.error('Please provide an application URL');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        salary_min: formData.salary_min ? parseInt(formData.salary_min) : null,
        salary_max: formData.salary_max ? parseInt(formData.salary_max) : null
      };

      if (isEditMode) {
        await axios.put(`${API}/jobs/${jobId}`, payload);
        toast.success('Job listing updated!');
        navigate(`/jobs/${jobId}`);
      } else {
        await axios.post(`${API}/jobs`, payload);
        toast.success('Job posted! It will be visible after admin approval.');
        navigate('/jobs');
      }
    } catch (error) {
      // Handle Pydantic validation errors (422) which return an array
      const detail = error.response?.data?.detail;
      let errorMessage = 'Failed to post job';
      
      if (Array.isArray(detail)) {
        errorMessage = detail[0]?.msg || detail[0]?.message || errorMessage;
      } else if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (typeof detail === 'object' && detail?.msg) {
        errorMessage = detail.msg;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <button onClick={() => navigate('/jobs')} className="inline-flex items-center text-muted-foreground hover:text-foreground mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Jobs
          </button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                <Briefcase className="w-8 h-8 text-primary" />
              </div>
              <h1 className="text-3xl font-heading font-bold mb-2">{isEditMode ? 'Edit Job' : 'Post a Job'}</h1>
              <p className="text-muted-foreground">Reach thousands of talented expats worldwide</p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 border-2 border-border space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h2 className="text-xl font-bold">Basic Information</h2>
                
                <div>
                  <Label htmlFor="title">Job Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Senior Software Engineer"
                    className="mt-1"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="company_name">Company Name *</Label>
                    <Input
                      id="company_name"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      placeholder="Your company name"
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="category">Category *</Label>
                    <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Job Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe the role, responsibilities, and what makes this opportunity great..."
                    className="mt-1 min-h-[150px]"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="requirements">Requirements</Label>
                  <Textarea
                    id="requirements"
                    value={formData.requirements}
                    onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                    placeholder="List the skills, experience, and qualifications needed..."
                    className="mt-1 min-h-[100px]"
                  />
                </div>
              </div>

              {/* Job Details */}
              <div className="space-y-4 pt-6 border-t border-border">
                <h2 className="text-xl font-bold">Job Details</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Job Type *</Label>
                    <Select value={formData.job_type} onValueChange={(v) => setFormData({ ...formData, job_type: v })}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {jobTypes.map((type) => (
                          <SelectItem key={type} value={type} className="capitalize">{type.replace('-', ' ')}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Work Arrangement *</Label>
                    <Select value={formData.work_arrangement} onValueChange={(v) => setFormData({ ...formData, work_arrangement: v })}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {workArrangements.map((arr) => (
                          <SelectItem key={arr} value={arr} className="capitalize">{arr}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="e.g., Bangkok"
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="country">Country *</Label>
                    <Input
                      id="country"
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      placeholder="e.g., Thailand"
                      className="mt-1"
                      required
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2 pt-2">
                  <Checkbox
                    id="visa"
                    checked={formData.visa_sponsorship}
                    onCheckedChange={(checked) => setFormData({ ...formData, visa_sponsorship: checked })}
                  />
                  <Label htmlFor="visa" className="font-medium cursor-pointer">
                    This position offers visa sponsorship
                  </Label>
                </div>
              </div>

              {/* Salary */}
              <div className="space-y-4 pt-6 border-t border-border">
                <h2 className="text-xl font-bold">Compensation (Optional)</h2>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="salary_min">Min Salary</Label>
                    <Input
                      id="salary_min"
                      type="number"
                      value={formData.salary_min}
                      onChange={(e) => setFormData({ ...formData, salary_min: e.target.value })}
                      placeholder="50000"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="salary_max">Max Salary</Label>
                    <Input
                      id="salary_max"
                      type="number"
                      value={formData.salary_max}
                      onChange={(e) => setFormData({ ...formData, salary_max: e.target.value })}
                      placeholder="80000"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Currency</Label>
                    <CurrencyInput
                      value={formData.salary_currency}
                      onChange={(v) => setFormData({ ...formData, salary_currency: v })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Period</Label>
                    <Select value={formData.salary_period} onValueChange={(v) => setFormData({ ...formData, salary_period: v })}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="year">Per Year</SelectItem>
                        <SelectItem value="month">Per Month</SelectItem>
                        <SelectItem value="hour">Per Hour</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Application Method */}
              <div className="space-y-4 pt-6 border-t border-border">
                <h2 className="text-xl font-bold">How to Apply</h2>
                
                <div>
                  <Label>Application Method *</Label>
                  <Select value={formData.application_method} onValueChange={(v) => setFormData({ ...formData, application_method: v })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Apply via Email</SelectItem>
                      <SelectItem value="external_link">External Application Link</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.application_method === 'email' && (
                  <div>
                    <Label htmlFor="application_email">Application Email *</Label>
                    <Input
                      id="application_email"
                      type="email"
                      value={formData.application_email}
                      onChange={(e) => setFormData({ ...formData, application_email: e.target.value })}
                      placeholder="hr@company.com"
                      className="mt-1"
                    />
                  </div>
                )}

                {formData.application_method === 'external_link' && (
                  <div>
                    <Label htmlFor="application_url">Application URL *</Label>
                    <Input
                      id="application_url"
                      type="url"
                      value={formData.application_url}
                      onChange={(e) => setFormData({ ...formData, application_url: e.target.value })}
                      placeholder="https://company.com/careers/job-123"
                      className="mt-1"
                    />
                  </div>
                )}
              </div>

              <div className="pt-6">
                <Button type="submit" disabled={loading} className="w-full h-12 rounded-full bg-primary font-bold">
                  {loading ? (isEditMode ? 'Saving...' : 'Posting...') : (isEditMode ? 'Save Changes' : 'Post Job')}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CreateJob;
